import React from 'react'

/**
 * Theme utility functions for switching between light and dark themes
 */

export type Theme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'isms-admin-theme'

/**
 * Get the current theme from localStorage or default to light
 */
export const getCurrentTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light'

  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'dark' || stored === 'light') {
    return stored
  }

  return 'dark'
}

/**
 * Set the theme and persist it to localStorage
 */
export const setTheme = (theme: Theme): void => {
  if (typeof window === 'undefined') return

  // Update the data-theme attribute on the root element
  document.documentElement.setAttribute('data-theme', theme)

  // Persist to localStorage
  localStorage.setItem(THEME_STORAGE_KEY, theme)

  // Dispatch custom event for components that need to react to theme changes
  window.dispatchEvent(new CustomEvent('themeChange', { detail: { theme } }))
}

/**
 * Toggle between light and dark themes
 */
export const toggleTheme = (): Theme => {
  const currentTheme = getCurrentTheme()
  const newTheme: Theme = currentTheme === 'light' ? 'dark' : 'light'
  setTheme(newTheme)
  return newTheme
}

/**
 * Initialize theme on app start
 */
export const initializeTheme = (): void => {
  const theme = getCurrentTheme()
  setTheme(theme)
}

/**
 * React hook for theme management
 */
export const useTheme = () => {
  const [theme, setThemeState] = React.useState<Theme>(getCurrentTheme)

  React.useEffect(() => {
    const handleThemeChange = (event: CustomEvent<{ theme: Theme }>) => {
      setThemeState(event.detail.theme)
    }

    window.addEventListener('themeChange', handleThemeChange as EventListener)
    return () => {
      window.removeEventListener('themeChange', handleThemeChange as EventListener)
    }
  }, [])

  const updateTheme = (newTheme: Theme) => {
    setTheme(newTheme)
  }

  const toggle = () => {
    return toggleTheme()
  }

  return {
    theme,
    setTheme: updateTheme,
    toggleTheme: toggle,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  }
}
